import React from 'react';
import { TextInput } from '@mantine/core';

// NOTA: Este componente requiere instalar @mantine/dates
// npm install @mantine/dates dayjs

export interface AfaDatePickerInputProps {
  /** Valor de la fecha seleccionada */
  value?: Date | null;
  
  /** Callback cuando cambia la fecha */
  onChange?: (value: Date | null) => void;
  
  defaultValue?: Date | null;

  /** Placeholder del input */
  placeholder?: string;
  
  /** Label del campo */
  label?: string;
  
  /** Descripción adicional */
  description?: string;
  
  /** Mensaje de error */
  error?: string;
  
  /** Si es requerido */
  required?: boolean;
  
  /** Si está deshabilitado */
  disabled?: boolean;
  
  /** Tamaño del input */
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
  
  /** Radio de bordes */
  radius?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
  
  /** Formato de fecha a mostrar */
  valueFormat?: string;
  
  /** Fecha mínima seleccionable */
  minDate?: Date;
  
  /** Fecha máxima seleccionable */
  maxDate?: Date;
  
  /** Si se puede limpiar la fecha */
  clearable?: boolean;
  
  /** Localización (idioma) */
  locale?: string;
  
  /** Días de la semana que están deshabilitados */
  excludeDate?: (date: Date) => boolean;
  
  /** Props adicionales para el DatePickerInput */
  datePickerProps?: any;
  
  /** Estilos personalizados */
  styles?: any;
  
  /** Clase CSS */
  className?: string;
  
  /** Nombre del input para formularios */
  name?: string;
  
  /** ID del input */
  id?: string;
}

/**
 * Wrapper del DatePickerInput de Mantine con defaults de AFA y estilos del tema
 * 
 * Ejemplos de uso:
 * 
 * // Básico
 * <AfaDatePickerInput
 *   placeholder="Selecciona una fecha"
 *   label="Fecha de nacimiento"
 * />
 * 
 * // Con formato personalizado
 * <AfaDatePickerInput
 *   valueFormat="DD/MM/YYYY"
 *   placeholder="dd/mm/aaaa"
 *   label="Fecha"
 * />
 * 
 * // Con restricciones
 * <AfaDatePickerInput
 *   minDate={new Date()}
 *   maxDate={new Date(2030, 11, 31)}
 *   excludeDate={(date) => date.getDay() === 0} // Excluir domingos
 *   label="Fecha de cita"
 * />
 * 
 * // Controlado
 * const [date, setDate] = useState<Date | null>(null);
 * <AfaDatePickerInput
 *   value={date}
 *   onChange={setDate}
 *   label="Fecha controlada"
 * />
 */
export function AfaDatePickerInput({
  value,
  onChange,
  defaultValue,
  placeholder = 'Selecciona una data',
  label,
  description,
  error,
  required = false,
  disabled = false,
  size = 'sm',
  radius = 'md',
  valueFormat = 'DD/MM/YYYY',
  minDate,
  maxDate,
  clearable = true,
  locale = 'es',
  excludeDate,
  datePickerProps,
  styles,
  className,
  name,
  id,
  ...otherProps
}: AfaDatePickerInputProps) {

  // IMPLEMENTACIÓN TEMPORAL con TextInput
  // Para usar DatePickerInput real, ejecutar: npm install @mantine/dates dayjs
  
  // Función helper para convertir Date a formato YYYY-MM-DD sin problemas de zona horaria
  const formatDateToInput = (date: Date | null): string => {
    if (!date) return '';
    try {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    } catch {
      return '';
    }
  };
  
  const [internalValue, setInternalValue] = React.useState<string>(() => {
    return formatDateToInput(value || null);
  });

  React.useEffect(() => {
    setInternalValue(formatDateToInput(value || null));
  }, [value]);

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const dateString = event.currentTarget.value;
    setInternalValue(dateString);
    
    if (onChange) {
      if (!dateString) {
        onChange(null);
      } else {
        try {
          // Dividir la fecha en partes (YYYY-MM-DD)
          const parts = dateString.split('-').map(Number);
          const [year, month, day] = parts;
          
          // Validar que tenemos todos los valores
          if (year && month && day && !isNaN(year) && !isNaN(month) && !isNaN(day)) {
            // Crear fecha usando el constructor con año, mes (0-indexado), día
            // Esto evita problemas de zona horaria
            const date = new Date(year, month - 1, day);
            onChange(date);
          } else {
            onChange(null);
          }
        } catch {
          onChange(null);
        }
      }
    }
  };

  // Función para forzar que el calendario se abra al hacer clic en cualquier parte del input
  const handleClick = (event: React.MouseEvent<HTMLInputElement>) => {
    if (!disabled) {
      event.currentTarget.showPicker?.();
    }
  };

  return (
    <TextInput
      type="date"
      {...(
        value !== undefined
          ? { value: internalValue }
          : { defaultValue: formatDateToInput(defaultValue || null) }
      )}
      {...(value !== undefined && { value: internalValue })}
      onChange={handleChange}
      onClick={handleClick}
      placeholder={placeholder}
      label={label}
      description={description}
      error={error}
      required={required}
      disabled={disabled}
      size={size}
      radius={radius}
      name={name}
      id={id}
      styles={{
        input: {
          cursor: disabled ? 'not-allowed' : 'pointer',
          borderColor: error ? 'var(--mantine-color-red-6)' : 'var(--mantine-color-brand-3)',
          '&:focus': {
            borderColor: 'var(--mantine-color-brand-5)',
            boxShadow: '0 0 0 2px var(--mantine-color-brand-1)',
          },
          '&:disabled': {
            backgroundColor: 'var(--mantine-color-gray-1)',
            color: 'var(--mantine-color-gray-6)',
          },
        },
        label: {
          color: 'var(--mantine-other-black)',
          fontWeight: 500,
        },
        description: {
          color: 'var(--mantine-color-neutral-6)',
        },
        error: {
          color: 'var(--mantine-color-red-6)',
        },
        ...styles,
      }}
      {...(className && { className })}
      {...otherProps}
    />
  );
}